import { useRef, useState } from "react";
import { Swiper as SwiperType } from "swiper";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Collection, FullItems } from "@/api";
import { ContentThumbnail, DripLockedModal } from "@/components";
import { useCurrentTabId, useFullItems, useNavigateToItem } from "@/hooks";
import { ChevronLeft, ChevronRight } from "@/icons";
import { cn, getContentThumbnailUrl } from "@/utils";
import { useResponsiveness, useUser } from "@/providers/context";

export interface HeroCarouselProps {
  collection: Pick<Collection, "Items" | "TabId">;
  fullItems: FullItems;
  "data-testid"?: string;
}

export const HeroCarousel = ({
  collection,
  fullItems,
  ...props
}: HeroCarouselProps) => {
  const { tabId } = useCurrentTabId();
  const { navigateToItem } = useNavigateToItem();
  const { getFullItem } = useFullItems(fullItems);
  const { colsPerRow } = useResponsiveness();
  const [isHovered, setIsHovered] = useState(false);
  const { isCollectionDripLocked } = useUser();
  const [dripLockedModalOpen, setDripLockedModalOpen] = useState(false);

  const swiperRef = useRef<SwiperType>();

  if (collection?.Items?.length === 0) {
    console.debug("Carousel hidden due to no supported items", { tabId });
    return null;
  }
  const items = collection?.Items;

  const isWideView = colsPerRow > 4;

  return (
    <div {...props}>
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Swiper
          className="w-full rounded-[16px]"
          loop={true}
          slidesPerView={1}
          modules={[Pagination]}
          pagination={{
            el: ".swiper-custom-pagination",
            clickable: true,
            bulletActiveClass:
              "swiper-pagination-bullet-active !bg-highlightPrimary",
            bulletClass: "swiper-pagination-bullet !bg-highlightSecondary",
          }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {items?.map((i) => {
            const fullItem = getFullItem(i);
            const isDripLocked =
              fullItem && "TabId" in fullItem
                ? isCollectionDripLocked(fullItem as Collection)
                : false;

            const thumbnail = !fullItem
              ? ""
              : isWideView
                ? getContentThumbnailUrl(fullItem, undefined, "wideLandscape")
                : getContentThumbnailUrl(fullItem, "large");

            return (
              <SwiperSlide
                className="flex items-center"
                onClick={() => {
                  if (isDripLocked) {
                    setDripLockedModalOpen(true);
                    return;
                  }
                  navigateToItem({
                    parentId: collection.TabId,
                    item: i,
                    fullItem,
                  });
                }}
                key={i.ChildId}
              >
                <ContentThumbnail
                  thumbnail={thumbnail}
                  className={cn(
                    "w-full cursor-pointer",
                    isWideView ? "aspect-wideLandscape" : "aspect-video",
                  )}
                  isDripLocked={isDripLocked}
                  isInHeroCarousel={true}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        {items.length > 1 && (
          <>
            <div
              className={cn(
                "absolute bottom-0 left-0 top-0 z-20 flex items-center opacity-0 transition-opacity duration-200 ease-in-out",
                isHovered && "opacity-100",
              )}
            >
              <div className="pointer-events-none absolute inset-0 rounded-bl-[16px] rounded-tl-[16px] bg-gradient-to-r from-gray-800/30 to-transparent"></div>
              <button
                className="relative ml-8 mr-16 flex h-8 w-8 items-center justify-center rounded-[50%] bg-surface-primary transition-opacity duration-200 ease-in-out hover:opacity-90 active:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  swiperRef.current?.slidePrev();
                }}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            </div>
            <div
              className={cn(
                "absolute bottom-0 right-0 top-0 z-20 flex items-center opacity-0 transition-opacity duration-200 ease-in-out",
                isHovered && "opacity-100",
              )}
            >
              <div className="pointer-events-none absolute inset-0 rounded-br-[16px] rounded-tr-[16px] bg-gradient-to-l from-gray-800/30 to-transparent"></div>
              <button
                className="relative ml-16 mr-8 flex h-8 w-8 items-center justify-center rounded-[50%] bg-surface-primary transition-opacity duration-200 ease-in-out hover:opacity-90 active:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  swiperRef.current?.slideNext();
                }}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          </>
        )}
      </div>
      <div
        className="swiper-custom-pagination mt-6 flex justify-center"
        data-testid={`${props["data-testid"]}-pagination`}
      />
      <DripLockedModal
        open={dripLockedModalOpen}
        setOpen={setDripLockedModalOpen}
      />
    </div>
  );
};
